import React, { Suspense, useCallback, useEffect, useState } from 'react';
import './App.css';
import { Route, Switch } from 'react-router-dom';
import { ConnectedRouter, replace } from 'connected-react-router';
import { dependencies } from '../index';
import ErrorBoundary from './ErrorBoundary/ErrorBoundary';
import { useDispatch, useSelector } from 'react-redux';
import { setActiveCountry } from '../redux/location/locationActions';
import { fetchCountries } from '../redux/country/countryActions';
import {
  getLandingCountry,
  FALLBACK_COUNTRIES,
  findCountryByUrlPath,
  resolveCountryFromPath,
  swapCountryInPath,
} from '../utils/country';
import { getSpaceActivities, getHomeCategories } from './Result/ResultAction';
import * as ReactGA from 'react-ga';
import ReactPixel from 'react-facebook-pixel';
import { Helmet } from 'react-helmet';
import 'font-awesome/css/font-awesome.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import AppRoutes from './AppRoutes';
import { getHomePageData } from './Home/HomeAction';
import { FACEBOOK_META_TAG } from '../settings';
import { getUserDetailByToken, removeAuthDetails, updateReduxWithAuthDetails } from '../redux/auth/authAction';
import { getAuthToken, hasValidAppSession, invalidateForeignAppSession, persistAuthSession } from '../utils/authSession';
import MandatoryChangePasswordModal from './Common/MandatoryChangePasswordModal';
import ServerStatusGuard from './ServerStatusGuard/ServerStatusGuard';
import { loadGoogleMapsScript } from '../utils/googleMapsLoader';
import PageLoader from './Common/Loaders/PageLoader';


function App() {
  const dispatch = useDispatch();

  const mockFunction = useCallback(() => {
  });
  const pathName = useSelector((state) => state?.router?.location?.pathname || '');
  const authDetails = useSelector((state) => state?.auth?.loginDetails?.data?.token || null);
  const countryLongName = useSelector((state) => state?.locationMeta?.data?.country_longCode?.replace(" ", "-") || null);
  const countryShortCode = useSelector(state => state?.locationMeta?.data?.country_shortCode);
  const countries = useSelector((state) => state?.countries?.data || []);
  const countriesResolved = useSelector((state) => state?.countries?.resolved);
  const [setectedCountry, setSelectedCountry] = useState(null)
  const [isAuthBootstrapComplete, setIsAuthBootstrapComplete] = useState(false);
  const user = useSelector((state) => state?.auth?.loginDetails?.data?.user || state?.auth?.userDetailsByToken?.details || null);
  const [showMandatoryPasswordChange, setShowMandatoryPasswordChange] = useState(false);

  useEffect(() => {
    if (user && user.mustChangePassword) {
      setShowMandatoryPasswordChange(true);
    } else {
      setShowMandatoryPasswordChange(false);
    }
  }, [user]);

  useEffect(() => {
    loadGoogleMapsScript().catch(() => {
      // Maps widgets degrade gracefully when the script cannot be loaded.
    });
  }, []);

  useEffect(() => {
    if (countryShortCode) {
      dispatch(getHomePageData(countryShortCode));
      dispatch(getSpaceActivities(countryShortCode));
      dispatch(getHomeCategories(countryShortCode));
      setSelectedCountry(countryShortCode);
    }
  }, [countryShortCode, authDetails]);

  useEffect(() => {
    ReactGA.pageview(pathName);
    ReactPixel.pageView();
  }, [pathName]);

  useEffect(() => {
    let isMounted = true;

    const completeBootstrap = () => {
      if (isMounted) {
        setIsAuthBootstrapComplete(true);
      }
    };

    const syncAuthState = (token) => {
      if (!token) {
        return;
      }

      persistAuthSession(token);
      dispatch(updateReduxWithAuthDetails({ token }));
      dispatch(getUserDetailByToken(token));
    };

    const bootstrapAuth = () => {
      if (invalidateForeignAppSession()) {
        dispatch(removeAuthDetails());
      }

      const searchParams = new URLSearchParams(window.location.search);
      const tokenFromUrl = searchParams.get('token');

      if (tokenFromUrl) {
        syncAuthState(tokenFromUrl);
        searchParams.delete('token');

        const nextSearch = searchParams.toString();
        const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash || ''}`;
        window.history.replaceState({}, document.title, nextUrl);
        completeBootstrap();
        return;
      }

      if (hasValidAppSession() && (getAuthToken() || authDetails)) {
        if (getAuthToken() && !authDetails) {
          dispatch(updateReduxWithAuthDetails({ token: getAuthToken() }));
        }
        completeBootstrap();
        return;
      }

      completeBootstrap();
    };

    bootstrapAuth();

    return () => {
      isMounted = false;
    };
  }, [authDetails, dispatch]);

  useEffect(() => {
    dispatch(fetchCountries());
  }, [dispatch]);

  // The URL is the source of truth for the market. /all/... is deliberately country agnostic
  // and keeps whatever market is already active.
  // Depend on pathName (not countryShortCode) so a dropdown switch that updates Redux before
  // navigation cannot get immediately overwritten by the still-old URL.
  // Wait for the active-country registry first: the admin can deactivate a market, and the
  // bundled list cannot tell, so resolving early would briefly show a deactivated market.
  useEffect(() => {
    if (!countriesResolved) {
      return;
    }

    const registry = countries?.length ? countries : FALLBACK_COUNTRIES;
    const { country, segment, isCountryAgnostic, isKnown } = resolveCountryFromPath(pathName, registry);

    if (country) {
      dispatch(setActiveCountry(country));
      return;
    }

    if (isCountryAgnostic) {
      if (!countryShortCode) {
        dispatch(setActiveCountry(getLandingCountry(registry)));
      }
      return;
    }

    // No live market in the URL (none given, unknown, or deactivated), so pick one from the
    // visitor's time zone.
    if (!isKnown) {
      const landing = getLandingCountry(registry);
      if (landing) {
        dispatch(setActiveCountry(landing));
        // A deactivated market's spaces and cities do not exist in another market, so send the
        // visitor to the landing market's home instead of keeping the rest of the path.
        const isDeactivatedMarket = !!findCountryByUrlPath(FALLBACK_COUNTRIES, segment);
        dispatch(replace(isDeactivatedMarket ? `/${landing.urlPath}` : swapCountryInPath(pathName, landing)));
      }
    }
  }, [pathName, countries, countriesResolved, dispatch]);

  useEffect(() => {
    if (authDetails && hasValidAppSession() && !getAuthToken()) {
      if (authDetails) {
        persistAuthSession(authDetails);
      }
    }
  }, [authDetails]);

  return (
    <div className={`App ms-styles-wrapper ${dependencies.history.location.pathname.includes('/spaces') ? 'space-page' : ''}`}>
      <Helmet>
        <link rel="canonical" href={`https://millionspaces.com${pathName}`} />
        {pathName === '/' + countryLongName &&
          <meta name="facebook-domain-verification" content={FACEBOOK_META_TAG} />
        }
      </Helmet>
      <>
        <ErrorBoundary>
          <MandatoryChangePasswordModal
            visible={showMandatoryPasswordChange}
            onClose={() => setShowMandatoryPasswordChange(false)}
          />
          <ServerStatusGuard>
            <ConnectedRouter history={dependencies.history}>
              <Suspense fallback={<PageLoader />}>
                <Switch>
                  <Route
                      path="/:country"
                      component={AppRoutes}
                   />
                </Switch>
              </Suspense>
            </ConnectedRouter>
          </ServerStatusGuard>
        </ErrorBoundary>
      </>
    </div>
  );
}

export default App;
